import {
    addDays as fnsAddDays,
    addMonths as fnsAddMonths,
    addWeeks as fnsAddWeeks,
    addYears as fnsAddYears,
    differenceInDays as fnsDifferenceInDays,
    differenceInMonths as fnsDifferenceInMonths,
    differenceInSeconds as fnsDifferenceInSeconds,
    endOfDay as fnsEndOfDay,
    endOfMonth as fnsEndOfMonth,
    endOfWeek as fnsEndOfWeek,
    formatDistance,
    isAfter as fnsIsAfter,
    isBefore as fnsIsBefore,
    parse,
    startOfDay as fnsStartOfDay,
    startOfMonth as fnsStartOfMonth,
    startOfWeek as fnsStartOfWeek,
    subDays as fnsSubDays,
    subMonths as fnsSubMonths,
    subSeconds as fnsSubSeconds,
    subWeeks as fnsSubWeeks,
    subYears as fnsSubYears
} from 'date-fns';
import { format, formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

const UTC_END = 'T00:00:00.000+00:00';
const FORMAT_UTC_END = "'T'HH:mm:ss.SSSxxx";

const dateFormatLong = 'EEE, MMM d, yyyy';
const dateFormatShort = 'MMM d, yyyy';
const time = 'h:mm a';

export const DATE_FORMAT = {
    DISPLAY_DATE_LONG: dateFormatLong,
    DISPLAY_DATE_SHORT: dateFormatShort,
    DISPLAY_TIME: time,
    DISPLAY_FULL: `${dateFormatLong} 'at' ${time}`,
    DISPLAY_FULL_SHORT: `${dateFormatShort} 'at' ${time}`,
    DISPLAY_MONTH: 'MMM-yy',

    QDATE: 'yyyy/MM/dd',
    TIMESTAMP: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
    LOCAL_DATE: 'yyyy-MM-dd',
    LOCAL_DATE_PICKER: 'MMM D, YYYY'
};

function utc(date: Date): Date {
    return zonedTimeToUtc(date, Intl.DateTimeFormat().resolvedOptions().timeZone);
}

export function addDays(date: Date, amount: number): Date {
    return fnsAddDays(date, amount);
}

export function addWeeks(date: Date, amount: number): Date {
    return fnsAddWeeks(date, amount);
}

export function addMonths(date: Date, amount: number): Date {
    return fnsAddMonths(date, amount);
}

export function addYears(date: Date, amount: number): Date {
    return fnsAddYears(date, amount);
}

export function differenceInDays(dateLeft: Date, dateRight: Date): number {
    return fnsDifferenceInDays(dateLeft, dateRight);
}

export function differenceInMonths(dateLeft: Date, dateRight: Date): number {
    return fnsDifferenceInMonths(dateLeft, dateRight);
}

export function differenceInSeconds(dateLeft: Date, dateRight: Date): number {
    return fnsDifferenceInSeconds(dateLeft, dateRight);
}

export function endOfDay(date: Date): Date {
    return fnsEndOfDay(date);
}

export function endOfMonth(date: Date): Date {
    return fnsEndOfMonth(date);
}

export function endOfWeek(date: Date): Date {
    return fnsEndOfWeek(date, { weekStartsOn: 1 });
}

export function isAfter(date: Date, dateToCompare: Date): boolean {
    return fnsIsAfter(date, dateToCompare);
}

export function isBefore(date: Date, dateToCompare: Date): boolean {
    return fnsIsBefore(date, dateToCompare);
}

export function subDays(date: Date, amount: number): Date {
    return fnsSubDays(date, amount);
}

export function subMonths(date: Date, amount: number): Date {
    return fnsSubMonths(date, amount);
}

export function subYears(date: Date, amount: number): Date {
    return fnsSubYears(date, amount);
}

export function subSeconds(date: Date, amount: number): Date {
    return fnsSubSeconds(date, amount);
}

export function subWeeks(date: Date, amount: number): Date {
    return fnsSubWeeks(date, amount);
}

export function startOfDay(date: Date): Date {
    return fnsStartOfDay(date);
}

export function startOfMonth(date: Date): Date {
    return fnsStartOfMonth(date);
}

export function startOfWeek(date: Date): Date {
    return fnsStartOfWeek(date, { weekStartsOn: 1 });
}

export function formatDateTime(date: Date, withDay: boolean = false, withTime: boolean = false): string {
    let readableDate: string;
    if (withDay) {
        if (withTime) {
            readableDate = format(date, DATE_FORMAT.DISPLAY_FULL);
        } else {
            readableDate = format(date, DATE_FORMAT.DISPLAY_DATE_LONG);
        }
    } else if (withTime) {
        readableDate = format(date, DATE_FORMAT.DISPLAY_FULL_SHORT);
    } else {
        readableDate = format(date, DATE_FORMAT.DISPLAY_DATE_SHORT);
    }
    return readableDate;
}

export function formatDateTimeSmall(date: Date): string {
    return format(date, DATE_FORMAT.DISPLAY_MONTH);
}

export function formatDateTimeTime(date: Date): string {
    return format(date, DATE_FORMAT.DISPLAY_TIME);
}

export function formatDateTimeUtc(date: Date, withDay: boolean = false): string {
    let readableDate: string;
    if (withDay) {
        readableDate = formatInTimeZone(date, 'UTC', DATE_FORMAT.DISPLAY_DATE_LONG);
    } else {
        readableDate = formatInTimeZone(date, 'UTC', DATE_FORMAT.DISPLAY_DATE_SHORT);
    }
    return readableDate;
}

export function formatLocalDate(date: Date, withDay: boolean = false): string {
    if (withDay) {
        return formatInTimeZone(date, 'UTC', DATE_FORMAT.DISPLAY_DATE_LONG);
    } else {
        return formatInTimeZone(date, 'UTC', DATE_FORMAT.DISPLAY_DATE_SHORT);
    }
}

export function getDateTime(value?: string, dateFormat = DATE_FORMAT.TIMESTAMP): Date {
    if (value === undefined) {
        return new Date();
    } else {
        return parse(value, dateFormat, new Date());
    }
}

export function getDateTimeUtc(value?: string, dateFormat = DATE_FORMAT.TIMESTAMP): Date {
    if (value === undefined) {
        return utc(new Date());
    } else {
        if (dateFormat !== DATE_FORMAT.TIMESTAMP) {
            return utc(parse(value + UTC_END, dateFormat + FORMAT_UTC_END, new Date()));
        } else {
            return utc(parse(value, dateFormat, new Date()));
        }
    }
}

export function getLocalDate(value?: string, dateFormat = DATE_FORMAT.LOCAL_DATE): Date {
    if (value === undefined) {
        return utc(new Date());
    } else {
        return utc(parse(value + UTC_END, dateFormat + FORMAT_UTC_END, new Date()));
    }
}

export function getTimeStamp(date: Date): string {
    return formatInTimeZone(date, 'UTC', DATE_FORMAT.TIMESTAMP);
}

export function getTimeStampLocalDate(date: Date): string {
    return formatInTimeZone(date, 'UTC', DATE_FORMAT.LOCAL_DATE);
}

export function getSinceDate(value: string, dateFormat = DATE_FORMAT.TIMESTAMP): string {
    const date = getDateTime(value, dateFormat);
    return formatDistance(date, new Date(), { addSuffix: true });
}

export function getTimeLeft(value: string): { label: string; negative: boolean } {
    if (!value) {
        return { label: '', negative: false };
    }
    const now = startOfDay(new Date());
    const theDate = startOfDay(getLocalDate(value));
    const durationTime = differenceInDays(theDate, now);
    if (durationTime < -1) {
        return { label: `${Math.floor(Math.abs(durationTime))} days overdue`, negative: true };
    } else if (durationTime === -1) {
        return { label: `${Math.floor(Math.abs(durationTime))} day overdue`, negative: true };
    } else if (durationTime === 1) {
        return { label: `${Math.floor(durationTime)} day left`, negative: false };
    } else {
        return { label: `${Math.floor(durationTime)} days left`, negative: false };
    }
}
